import { isNotEmpty, useForm } from '@mantine/form'
import {
  IUpdateClinicMembershipLocationDto,
  ProviderDto,
} from '@v2/domains/clinics/clinics.type'
import { useClinicsMutations } from '@v2/domains/clinics/hooks/useClinicsMutations.hook'
import { useGetLocationsQuery } from '@v2/domains/location/api/location.api'
import { useGetUsersQuery } from '@v2/domains/user/api/user.api'
import { UserType } from '@v2/domains/user/user.types'
import { useState } from 'react'

import {
  AddDoctorClinicLocationDto,
  CreateDoctorClinicDto,
} from 'src/api/generated'

import { IProviderFullForm } from '../membership.types'
import { useMembershipMutations } from './useMembershipMutations.hook'

interface AddDoctorLocationProps {
  clinicId: string
  provider?: ProviderDto
}

const useAddDoctorLocation = ({
  clinicId,
  provider,
}: AddDoctorLocationProps) => {
  const {
    addDoctorLocation,
    updateMembershipLocation,
    isLoadingAddDoctorLocation,
    isLoadingMembershipLocation,
  } = useMembershipMutations()

  const { createClinicDoctor, isLoadingCreateDoctor } = useClinicsMutations()

  const { isSuccess: isSuccessUser, data: providers } = useGetUsersQuery({
    organizationId: '',
    userType: UserType['DOCTOR'],
  })

  const { data: locations, isSuccess: isSuccessLocation } =
    useGetLocationsQuery({ clinicId })

  const providerData =
    providers?.records.map((user) => ({ label: user.name, value: user.id })) ??
    []

  const locationData =
    locations?.records.map((location) => ({
      label: location.name,
      value: location.id,
    })) ?? []

  const [showProviderInformation, setShowProviderInformation] =
    useState<boolean>(false)

  const form = useForm<IProviderFullForm>({
    mode: 'uncontrolled',
    initialValues: {
      userId: provider?.providerId ?? '',
      clinicId,
      locationId: provider?.locationId ?? '',
      specialty: provider?.metaData?.specialty,
      firstName: '',
      lastName: '',
      email: '',
    },
    validateInputOnBlur: true,
    validate: {
      locationId: isNotEmpty('Location is required!'),
      specialty: isNotEmpty('Specialty is required!'),
      userId: (value) =>
        !showProviderInformation
          ? isNotEmpty('Provider is required!')(value)
          : null,
      firstName: (value) =>
        showProviderInformation
          ? isNotEmpty('First name is required!')(value)
          : null,
      lastName: (value) =>
        showProviderInformation
          ? isNotEmpty('Last name is required!')(value)
          : null,
      email: (value) =>
        showProviderInformation
          ? isNotEmpty('Email is required!')(value)
          : null,
    },
  })

  const onSubmit = () => {
    form.onSubmit(async (values) => {
      if (provider?.membershipLocationId) {
        const newValues: IUpdateClinicMembershipLocationDto = {
          membershipLocationId: provider.membershipLocationId,
          locationId: values.locationId,
          specialty: values.specialty,
        }
        await updateMembershipLocation(newValues)
        return
      }
      if (values.userId === '') {
        const newValues: CreateDoctorClinicDto = {
          clinicId: values.clinicId,
          locationId: values.locationId,
          specialty: values.specialty,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        }
        await createClinicDoctor(newValues)
        return
      }
      const newValues: AddDoctorClinicLocationDto = {
        clinicId: values.clinicId,
        locationId: values.locationId,
        specialty: values.specialty,
        userId: values.userId,
      }
      await addDoctorLocation(newValues)
      return
    })()
  }

  const handleAddManualyProvider = () => {
    form.setFieldValue('userId', '')
    setShowProviderInformation(true)
  }

  return {
    form,
    onSubmit,
    isLoading:
      isLoadingAddDoctorLocation ||
      isLoadingCreateDoctor ||
      isLoadingMembershipLocation,
    providerData,
    locationData,
    isSuccessData: isSuccessUser && isSuccessLocation,
    showProviderInformation,
    handleAddManualyProvider,
  }
}

export default useAddDoctorLocation
